// Money renders in its ACTUAL currency (platform home currency = CAD; ledger
// lines carry basis_currency, payouts carry currency). en-CA locale keeps CAD
// as a plain "$" while foreign currencies get an explicit prefix ("US$", "€"),
// so a CAD platform never silently labels amounts as USD (2026-07-23 release
// audit — the old formatter hardcoded USD).
const DEFAULT_CURRENCY = "CAD";
const moneyFormatters = new Map();
const money = (currency, dp) => {
  const cur = String(currency || DEFAULT_CURRENCY).toUpperCase();
  const key = `${cur}|${dp}`;
  if (!moneyFormatters.has(key)) {
    let f;
    try {
      f = new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: cur,
        maximumFractionDigits: dp,
        minimumFractionDigits: dp,
      });
    } catch (_) {
      f = new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: DEFAULT_CURRENCY,
        maximumFractionDigits: dp,
        minimumFractionDigits: dp,
      });
    }
    moneyFormatters.set(key, f);
  }
  return moneyFormatters.get(key);
};

// Symbol-free, for an amount whose denomination is genuinely unknown. Two
// decimals so it still reads as money rather than a count.
const numPlain = new Intl.NumberFormat("en-CA", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numCompact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const fmt$ = (n, dp = 0, currency = DEFAULT_CURRENCY) => {
  if (n == null || Number.isNaN(Number(n))) return money(currency, dp).format(0);
  return money(currency, dp).format(Number(n));
};

/**
 * Same as fmt$, except ABSENT and ZERO are not the same answer.
 *
 * fmt$ maps null to $0.00, which stayed invisible while every campaign type had
 * its amounts filled in. A FUND does not: goal_invest_amnt and
 * outside_roots_investment are NULL by design, because a fund states a target
 * raise and a maximum offering instead. So the screen an admin approves an
 * offering on reported "Goal Amount: $0.00" — a confident, specific, WRONG
 * number, and indistinguishable from a genuine zero goal.
 *
 * A sibling rather than a change to fmt$, which has callers that legitimately
 * want a missing figure to total as zero.
 *
 * The wording matches the "— not set" this page already uses for an unset
 * currency, so the two absences read the same way to a reviewer.
 */
export const fmt$OrUnset = (n, dp = 0, currency = DEFAULT_CURRENCY) => {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "— not set";
  return money(currency, dp).format(Number(n));
};

/**
 * Money with its DENOMINATION stated, for screens where reading the wrong
 * currency costs something.
 *
 * Two problems this solves, both of them live on the campaign approval screen.
 *
 * 1. UNDER en-CA, CAD RENDERS AS A BARE "$". That is right for a single-currency
 *    page and wrong for one that shows CAD and USD offerings side by side: the
 *    marker that distinguishes them is the ABSENCE of "US", which is not a marker
 *    a reviewer notices. Appending the ISO code is mildly redundant for USD and
 *    unambiguous for both, which is the trade an approval screen wants.
 *
 * 2. AN UNKNOWN CURRENCY WAS SILENTLY RENDERED AS CAD. money() falls back to
 *    DEFAULT_CURRENCY whenever it is handed a falsy value, and campaign_details
 *    .currency_type is nullable — CampaignViewPage passes
 *    `userInfo?.currency_type || undefined`. So a USD fund whose currency_type was
 *    never set displayed its target raise as a bare "$" figure that an approver
 *    reads as Canadian dollars. That is exactly the class of error fmt$OrUnset was
 *    written to remove — a confident, specific, WRONG answer — one field over.
 *    Here an unknown currency is stated as unknown, and the number is still shown
 *    rather than hidden, because the figure itself is real.
 *
 * Absent stays "— not set", identical to fmt$OrUnset, so the two read the same.
 *
 * This replaces a hand-written `{fmt$OrUnset(...)}{cur ? ` ${cur}` : ""}` that
 * FeeObligationPanel was carrying inline. One helper rather than a pattern each
 * caller re-derives — the drift class this console has been bitten by before.
 */
export const fmt$Denominated = (n, dp = 2, currency) => {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "— not set";
  const cur = String(currency == null ? "" : currency).trim().toUpperCase();
  if (!cur) return `${numPlain.format(Number(n))} (currency not set)`;
  return `${money(cur, dp).format(Number(n))} ${cur}`;
};

export const fmt$Compact = (n, currency = DEFAULT_CURRENCY) => {
  if (n == null || Number.isNaN(Number(n))) return money(currency, 0).format(0);
  const cur = String(currency || DEFAULT_CURRENCY).toUpperCase();
  const key = `compact|${cur}`;
  if (!moneyFormatters.has(key)) {
    moneyFormatters.set(
      key,
      new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: cur,
        notation: "compact",
        maximumFractionDigits: 2,
      })
    );
  }
  return moneyFormatters.get(key).format(Number(n));
};

// "CA$12.00 · US$5.00" — one figure per currency, never a cross-FX sum.
// rows: [{currency, <field>: amount}]; falls back to a single default-currency
// figure when no per-currency data is present.
export const fmtMoneyByCurrency = (rows, field, fallback, dp = 2) => {
  const nonZero = (rows || []).filter((r) => Number(r?.[field] || 0) !== 0);
  if (!nonZero.length) return fmt$(fallback || 0, dp);
  return nonZero.map((r) => fmt$(r[field], dp, r.currency)).join(" · ");
};

export const fmtNum = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "0";
  return new Intl.NumberFormat("en-US").format(Number(n));
};

export const fmtNumCompact = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "0";
  return numCompact.format(Number(n));
};

export const fmtPct = (n, dp = 1) => {
  if (n == null || Number.isNaN(Number(n))) return "0%";
  const v = Number(n);
  return (v >= 0 ? "+" : "") + v.toFixed(dp) + "%";
};

export const fmtBps = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "0bps";
  const v = Number(n);
  return (v >= 0 ? "+" : "") + v.toFixed(0) + "bps";
};

export const fmtRelative = (timestamp) => {
  if (!timestamp) return "";
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
};
